import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { Instance } from "../../types/cloud";
import { StatusBadge } from "./StatusBadge";
import { ProvisioningProgress } from "./ProvisioningProgress";
import { ConfirmDialog } from "./ConfirmDialog";
import { startInstance, stopInstance, deleteInstance } from "../../lib/api/cloud";

interface InstanceCardProps {
  instance: Instance;
  onRefresh: () => void;
}

function formatRelativeTime(dateStr: string | null): string {
  if (!dateStr) return "N/A";
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function InstanceCard({ instance, onRefresh }: InstanceCardProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState<"stop" | "delete" | null>(null);

  const handleAction = async (action: "start" | "stop" | "delete") => {
    setLoading(true);
    try {
      if (action === "start") await startInstance(instance.id);
      else if (action === "stop") await stopInstance(instance.id);
      else if (action === "delete") await deleteInstance(instance.id);
      onRefresh();
    } catch {
      // Error handling is simplified for now
    } finally {
      setLoading(false);
      setConfirmAction(null);
    }
  };

  return (
    <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-[#f5f5f5]">
            {instance.subdomain}.cc.somebugs.com
          </h2>
          <p className="text-[#999] text-sm mt-0.5">{t('dashboard.instance.label')}</p>
        </div>
        <StatusBadge status={instance.status} size="md" />
      </div>

      {instance.status === "provisioning" && (
        <div className="mb-4">
          <ProvisioningProgress />
        </div>
      )}

      {(instance.status === "running" || instance.status === "stopped") && (
        <div className="grid grid-cols-2 gap-4 mb-4">
          {instance.providerIp && (
            <div>
              <span className="text-[#666] text-xs uppercase tracking-wider">{t('dashboard.instance.ip')}</span>
              <p className="text-[#f5f5f5] text-sm font-mono">{instance.providerIp}</p>
            </div>
          )}
          {instance.plan && (
            <div>
              <span className="text-[#666] text-xs uppercase tracking-wider">{t('dashboard.instance.plan')}</span>
              <p className="text-[#f5f5f5] text-sm">
                {instance.plan.name} ({instance.plan.cpuCores} CPU, {instance.plan.ramMb / 1024} GB
                RAM, {instance.plan.diskGb} GB)
              </p>
            </div>
          )}
          {instance.lastHealthAt && (
            <div>
              <span className="text-[#666] text-xs uppercase tracking-wider">
                {t('dashboard.instance.lastHealthCheck')}
              </span>
              <p className="text-[#f5f5f5] text-sm">{formatRelativeTime(instance.lastHealthAt)}</p>
            </div>
          )}
          <div>
            <span className="text-[#666] text-xs uppercase tracking-wider">{t('dashboard.instance.created')}</span>
            <p className="text-[#f5f5f5] text-sm">{formatRelativeTime(instance.createdAt)}</p>
          </div>
        </div>
      )}

      {instance.status === "error" && instance.errorMessage && (
        <div className="mb-4 p-3 bg-red-500/5 border border-red-500/20 rounded text-red-400 text-sm">
          {instance.errorMessage}
        </div>
      )}

      <div className="flex gap-3 pt-2 border-t border-[#333]">
        {instance.status === "running" && (
          <>
            <a
              href={instance.url}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-blue-500/10 text-blue-400 rounded-lg text-sm font-medium hover:bg-blue-500/20 transition-colors"
            >
              {t('dashboard.instance.openCloseCode ADE')}
            </a>
            <button
              onClick={() => setConfirmAction("stop")}
              disabled={loading}
              className="px-4 py-2 bg-yellow-500/10 text-yellow-400 rounded-lg text-sm font-medium hover:bg-yellow-500/20 transition-colors disabled:opacity-40"
            >
              {t('dashboard.instance.stopInstance')}
            </button>
          </>
        )}
        {instance.status === "stopped" && (
          <>
            <button
              onClick={() => handleAction("start")}
              disabled={loading}
              className="px-4 py-2 bg-green-500/10 text-green-400 rounded-lg text-sm font-medium hover:bg-green-500/20 transition-colors disabled:opacity-40"
            >
              {t('dashboard.instance.startInstance')}
            </button>
            <button
              onClick={() => setConfirmAction("delete")}
              disabled={loading}
              className="px-4 py-2 bg-red-500/10 text-red-400 rounded-lg text-sm font-medium hover:bg-red-500/20 transition-colors disabled:opacity-40"
            >
              {t('dashboard.instance.deleteInstance')}
            </button>
          </>
        )}
        {instance.status === "error" && (
          <>
            <button
              onClick={() => handleAction("start")}
              disabled={loading}
              className="px-4 py-2 bg-blue-500/10 text-blue-400 rounded-lg text-sm font-medium hover:bg-blue-500/20 transition-colors disabled:opacity-40"
            >
              {t('dashboard.instance.retry')}
            </button>
            <button
              onClick={() => setConfirmAction("delete")}
              disabled={loading}
              className="px-4 py-2 bg-red-500/10 text-red-400 rounded-lg text-sm font-medium hover:bg-red-500/20 transition-colors disabled:opacity-40"
            >
              {t('dashboard.instance.delete')}
            </button>
          </>
        )}
      </div>

      <ConfirmDialog
        open={confirmAction === "stop"}
        title={t('dashboard.instance.stopConfirmTitle')}
        message={t('dashboard.instance.stopConfirmMessage')}
        confirmText={t('dashboard.instance.stopInstance')}
        danger
        onConfirm={() => handleAction("stop")}
        onCancel={() => setConfirmAction(null)}
      />

      <ConfirmDialog
        open={confirmAction === "delete"}
        title={t('dashboard.instance.deleteConfirmTitle')}
        message={t('dashboard.instance.deleteConfirmMessage')}
        confirmText={t('dashboard.instance.deleteInstance')}
        danger
        confirmInput={instance.subdomain}
        onConfirm={() => handleAction("delete")}
        onCancel={() => setConfirmAction(null)}
      />
    </div>
  );
}
